import { Slot } from "@radix-ui/react-slot";
import type { ReactNode } from "react";
import { cn } from "../lib/cn.ts";
import type { HTMLProps } from "../lib/props.ts";

export function SidebarMenu({
  class: klass,
  className,
  ...props
}: HTMLProps<"ul">) {
  return (
    <ul
      data-sidebar="menu"
      className={cn("flex w-full min-w-0 flex-col gap-1", klass, className)}
      {...props}
    />
  );
}

export function SidebarMenuItem({
  class: klass,
  className,
  ...props
}: HTMLProps<"li">) {
  return (
    <li
      data-sidebar="menu-item"
      className={cn("group/menu-item relative", klass, className)}
      {...props}
    />
  );
}

export interface SidebarMenuButtonProps {
  asChild?: boolean;
  isActive?: boolean;
  size?: "default" | "lg";
  children: ReactNode;
  className?: string;
  onClick?: () => void;
}

export function SidebarMenuButton({
  asChild = false,
  isActive = false,
  size = "default",
  children,
  className,
  onClick,
}: SidebarMenuButtonProps) {
  const Comp = asChild ? Slot : "button";
  return (
    <Comp
      type={asChild ? undefined : "button"}
      data-sidebar="menu-button"
      data-size={size}
      data-active={isActive || undefined}
      onClick={onClick}
      className={cn(
        "peer/menu-button flex w-full items-center gap-2 overflow-hidden rounded-xl text-left text-[13px] outline-none",
        "transition-[color,background-color,transform] duration-150 hover:translate-x-0.5",
        "hover:bg-muted hover:text-foreground focus-visible:ring-2 focus-visible:ring-ring",
        "disabled:pointer-events-none disabled:opacity-50",
        "data-[active=true]:bg-muted data-[active=true]:font-medium data-[active=true]:text-foreground",
        "[&>svg]:size-4 [&>svg]:shrink-0",
        size === "lg" ? "min-h-12 p-2.5" : "h-9 px-2.5",
        className,
      )}
    >
      {children}
    </Comp>
  );
}
